import React, { useCallback, useId, useState } from 'react';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend';
import AutoGrid from 'c-components/AutoGrid';
import { Box, Typography } from '@mui/material';
import type { TranslationPath } from 'c-translation';
import { useCommonTranslation } from 'c-translation';
import { Alert } from 'c-components/MuiOverrides';

type Props = {
    /** The file input's id. Defaults to one unique to this dropzone. */
    id?: string;
    dropzoneLabel?: TranslationPath;
    fileAccept: string;
    onFilesAdded: (files: File[]) => void;
    disabled?: boolean;
};
const isFilenameValid = filename => {
    const disallowedCharsRegex = /[\\/:*?"<>|~]/;
    return !disallowedCharsRegex.test(filename);
};

const FileUploadDropzone: React.FC<Props> = ({
    id,
    dropzoneLabel,
    fileAccept,
    onFilesAdded,
    disabled = false,
}) => {
    const generatedId = useId();
    const inputId = id ?? `file-upload-${generatedId}`;
    const t = useCommonTranslation();
    const label = useCommonTranslation(dropzoneLabel);
    const [error, setError] = useState<string | null>(null);
    const addFiles = useCallback(
        (files: File[]) => {
            setError(null);
            const validFiles = files?.filter(file => isFilenameValid(file?.name));
            if (validFiles.length > 0) {
                onFilesAdded(validFiles);
            }

            const invalidFilesCount = files.length - validFiles.length;
            if (invalidFilesCount > 0) {
                setError(
                    t('Component.FileUploadDropzone.invalidFilenames', {
                        invalidCount: invalidFilesCount,
                    }),
                );
            }
        },
        [onFilesAdded, t],
    );
    const onDrop = useCallback(
        (data: { files: File[] }) => {
            if (!disabled) {
                addFiles(data.files);
            }
        },
        [addFiles, disabled],
    );
    const onChange = useCallback(
        e => {
            const files: FileList = e.target.files;

            if (files.length > 0 && files.item(0)) {
                addFiles(Array.from(files));
                // reset selected files so the same file can be selected again and trigger change event
                e.target.value = '';
            }
        },
        [addFiles],
    );

    const [{ isOver, canDrop }, drop] = useDrop(
        {
            accept: NativeTypes.FILE,
            drop: onDrop,
            collect: monitor => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
        },
        [onDrop],
    );

    return (
        <>
            <AutoGrid spacing={2} xs={12} sx={{ opacity: disabled ? 0.5 : 1 }}>
                <label htmlFor={inputId} style={{ cursor: 'pointer' }}>
                    <Box display="none">
                        <input
                            accept={fileAccept}
                            type="file"
                            id={inputId}
                            multiple
                            onChange={onChange}
                            disabled={disabled}
                        />
                    </Box>
                    <Box
                        ref={drop}
                        mt={2}
                        p={4}
                        sx={{
                            borderWidth: 4,
                            borderStyle: 'dashed',
                            borderColor: !disabled
                                ? isOver && canDrop
                                    ? 'success.main'
                                    : 'primary.main'
                                : 'primary.main',
                        }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            variant="subtitle1"
                            color={
                                !disabled
                                    ? isOver && canDrop
                                        ? 'success.main'
                                        : 'textSecondary'
                                    : 'textSecondary'
                            }
                        >
                            {label}
                        </Typography>
                    </Box>
                </label>
            </AutoGrid>
            {error && <Alert severity="error">{error}</Alert>}
        </>
    );
};

const Wrapper: React.FC<Props> = props => (
    <DndProvider backend={HTML5Backend}>
        <FileUploadDropzone {...props} />
    </DndProvider>
);

export default Wrapper;
